/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AxiosResponse, AxiosError } from 'axios'
import { interpret, assign } from 'xstate'
import { createModel } from 'xstate/lib/model'

import { assign as immerAssign } from '@xstate/immer'

import {
  lateDivideTransaction,
  batchHandleTransactions,
  updateTransactionStatus,
  validateTransaction,
} from 'src/services/transactionService'
import { FustAmount } from 'src/types/fust'
import {
  FustTransaction,
  TransactionStatus,
  TransactionType,
} from 'src/types/transaction'
import { getSpawnedIssuanceTransactionsDataGridMachine } from 'src/utils'

import { issuanceTransactionsDataGridMachineService } from './issuanceTransactionsDataGridMachine'
import { ScanFustPassFlowMachineContext } from './scanFustPassFlowMachine'

export type IssuanceDockTransactionsMachineContext = {
  transactionsDataGridService: ReturnType<
    typeof getSpawnedIssuanceTransactionsDataGridMachine
  >
  scannedFustPassContext: ScanFustPassFlowMachineContext
  selectedTransaction?: FustTransaction
  updatedFustAmount: FustAmount[]
  transactionMode?: 'split' | 'edit'
  error?: string
}
export const issuanceDockTransactionsMachineModel = createModel(
  {
    transactionsDataGridService: issuanceTransactionsDataGridMachineService,
    scannedFustPassContext: {},
    updatedFustAmount: [],
    selectedTransaction: undefined,
    transactionMode: undefined,
    error: undefined,
  } as IssuanceDockTransactionsMachineContext,
  {
    events: {
      EDIT_TRANSACTION: (transaction: FustTransaction) => ({ transaction }),
      SPLIT_TRANSACTION: (transaction: FustTransaction) => ({ transaction }),
      ASSIGN_UPDATED_FUST_AMOUNT: (updatedFustAmount: FustAmount[]) => ({
        updatedFustAmount,
      }),
      ASSIGN_SELECTED_TRANSACTION: (transaction: FustTransaction) => ({ transaction }),
      HANDLE_DIVIDE_TRANSACTION: (
        txId: FustTransaction['id'],
        fustAmount: FustTransaction['fustAantallen']
      ) => ({
        txId,
        fustAmount,
      }),
      REVIEW_TRANSACTIONS: () => ({}),
      HANDLE_TRANSACTIONS: () => ({}),
      CONTINUE: () => ({}),
      CLOSE_FUST_PASS_NUMBER_DIALOG: () => ({}),
      VALIDATE_TRANSACTION: () => ({}),
      RESET_ERROR: () => ({}),
    },
  }
)

const modelAssign = issuanceDockTransactionsMachineModel.assign

export const issuanceDockTransactionsMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QEtawK4EMB2BjMAIgPa4DWAKgE46ya4AuyR2sAsnQBbLZgB0AbsjAB3blAD6AG1T1xRAGbj61FnUbNYAYgASAQQByBADIBRcQGUAkvoDip8eQBKB87oDC5SwHl9iUAAciWGR1bD8QAA9EACYADgBmXliARgA2ABZogAYATgB2dNSAVnScgBoQAE9EVOTo3iKs+Lz4rNSc9Lai+IBfHorUDBx8YjIqGjUmFnZcLh4BIVFsCWlYWQUlFVoGKa09Q3tWAFUjTwAFeycXd08fc3DA4NDwqIQ4xJSM7PzCkvKqxAldINZJFRrRTrJFLpPoDNBYPCEEgULaTDQzOZ8ShgQQiMSbCY7DTiYLLSRgTQmAiWcgOZz6Vweby+JAgR4hKYvRCxPI5JJ5PIZIopIr5HL-aoIVItXhQrLJAVZUV5aKpWKwkCDBEjZHjVRE6acbhYnGLfHKQmhWAksTkzTmC40unXJk+B5BDnMLkIHl8nmC9LC0FiiWA2KpXjROoq6J5WJFOrJDVa4ZIsaog1sI3zbG4pYSC36q02skU-bGMxXBk3Zn3Vns56s16+-kBoPK8UVSXxeKxXjxfKxcPpVrxVLtIrJ+Gp0Yoy27DHG3i4ZjyZCUAC2+NE9A44nk6DW4n8mDQ9qOACFWE63D4AGKWRysXS3fTiO9Hcy0s66cx1gIeo2oCvMknR5LKeTCgk4rSgk8Rdog8TJFkvA5FkkElHUqRNOk6r9Jq06IrOerbFai7zCu2Brpu24hHuB5HieZ5uEYXjmGYH5fuIP5-uI+hHKwF4mI44jUrorE2O6Tyck2iCgehEFQQOOSwT2CEILhyT9ryyRQgq0o5I0vT4SmRG6hmZHZnwsxgGQ24cJgsj0EQJLoAARlu9CaFJnphLJCDyeBCpKTBcZqQCAWdKheTJM0sTobyWSxIZU5DGZ6bzuiVm8BgHkhIwywEkWuyaBAzB8Nw-BEKQFWETqGXFVlsxLrlnkFQWFm7AglUkI5UwANpZAAuj5QGRDE8p9pkQYBgq4rwRFUJFLwgaxrpQ4Gd0eFwml9Vzo1hrNfMrX5eanUaJoYCUJQRCULw-iSI58i3RuvCmXtJFoodmI5e5bVnZlLDddgVW4H1zCDSN9aATJwETVCK3RDNGRzTkC2ShCfLtGO7SxUUGTxJOJl1Wm+2kQu2WQByhWFuTzCaI4JgAGqWCYADqzrVq6DKjbD42RQpwVDspqnozUOT1NK0RjlGA7pIUqXaqTn2ZuRfD8Jg0gQH1NPndgpXlW9IPVbVu3K3rWZHermvINr7VFXT2DA6D4PYJDvNev52QI9NKSzbyaPqeOiSE6qKkTr20Q5IrM7mYDls-RrWs6x1gOXddt33Y99DPZub0k8RFtqwINt2wDB3O71oTu9D0me3DbyTYjyPpKjYtSqC-btLUSPxVGJQx+lZNfQnS4QMggjj7rae-lYtjiEcZwEC+VLvp+tK6KwXhHPo5Ae35De6XUvDYWC7RZGtkfqQm4FtKkSNxAkLTy4PH1F9l4+T+XjuaLP1g2BYEwpgPCryrIyV8+9vRHxQj2eIrd74pGUqGDSrReC5F9OGXIdR4yv3NvHYun9bbfy+poJm4lLDL3IJWek4DmSQP8oFRSwtQpwSDqBBoMteQQjVLEaIuDC74Oyideg9tCEQDAA7EeBt5g9RqvnM2AiDqj2On9U6hUxESNpiPSuYNq7DXoQ3b2U1e76VbgHduSFEhoXlhLLIWROgjhwcTBRcclHF2EaIiettNEW3TjdO6D0novXkUrRRjtlF8A8fiDRkjMw6NdjXACdcD78yMc3P2KNzHqTHFpIckFBS6QlrpPhzjQmuPCcXXMZpp5KO8rXXy3psgJBWjFB+CZ8YtHUoqE+vISg9mQmHPIfR8LYCIOI+ArJ3p4LcdlPM+JVjrEUFozMBj+aZGvukW+yEVIJFqDkeKqR+HlJHpU00eIanhJLFAckqzXjYWBLGWWhko7IQHEHBSdjdLClPkUaWMJSmxwahU7KlFqJbkKjueih5ZBMQmUkhpDDYoRhKNEbIdi4q8NSNk+I9Rkq-KVGkUU2QChHKBSckFHBbKkHso5JQLlhG3LklhSMRQBSIKQgU9I6lkgqRWoGQmPKwSxEKKqUlw9VZCNUSI4hKz6ljRAs-E+gYwQ4qHFHBI3K8goUMpHFSLy0b4zFSrSyVtlxEA3A9MA9BICxKtIyt4SFlqYJih0UEmyRzcqHHyloaRxTxVaL8o179TVVPOanJR4gNzoEkIwS19rRQRgVDi6Wgo4GCmvgONBdQ0ZNFio8pMAKh7Gopqaqm9tlljQbHzEChLtKssaDY++44g48llN3XhyVpSQW2gRFxZKJWmqTrbFOtq+ZVvrvzJNuTug3xUipAUWKIrYRQjyO+F84EYqDYI01YiZWVphhOmt980GBlxrGYVw53l8iaM0dCoIEiQi3TM01UT1FePEaOjQ9qFSqiSMUgopRSiGXUlHPknQVUqWFMSgtO0yn9pNT9UN+ZP0sHtdLTuzqeWtxKAUdumzlrRmFTysUeKiawcBeKhDS4K27HEFdfxaHHVJGwi67D7r25I3Ao4wjt7U1PuBVbb9sRPXDJ6EAA */
  issuanceDockTransactionsMachineModel.createMachine(
    {
      context: issuanceDockTransactionsMachineModel.initialContext,
      tsTypes: {} as import('./issuanceDockTransactionsMachine.typegen').Typegen0,
      schema: {
        services: {} as {
          divideTransaction: { data: AxiosResponse<void> }
          handleTransactions: {
            data: void[]
          }
          lateDivideTransaction: { data: AxiosResponse<void> }
          validateTransaction: { data: AxiosResponse<FustAmount[]> }
        },
      },
      entry: modelAssign({
        transactionsDataGridService: ({ transactionsDataGridService }) =>
          transactionsDataGridService,
      }),
      predictableActionArguments: true,
      id: 'issuanceDockTransactionsMachine',
      initial: 'viewing_list_of_transactions',
      states: {
        viewing_list_of_transactions: {
          on: {
            REVIEW_TRANSACTIONS: {
              target: 'reviewing_transactions',
            },
          },
        },
        reviewing_transactions: {
          on: {
            EDIT_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'editing_transaction',
            },
            SPLIT_TRANSACTION: {
              actions: 'assignSelectedTransaction',
              target: 'dividing_transaction',
            },
            HANDLE_TRANSACTIONS: {
              target: 'confirming_with_fust_pass',
            },
          },
        },
        confirming_with_fust_pass: {
          on: {
            CONTINUE: {
              target: 'checking_what_to_submit',
            },
            CLOSE_FUST_PASS_NUMBER_DIALOG: {
              target: 'reviewing_transactions',
            },
          },
        },
        checking_what_to_submit: {
          always: [
            {
              cond: 'isNotSplittedFustAmount',
              target: 'submitting_transactions',
            },
            {
              cond: 'isSplittedFustAmount',
              target: 'submitting_divide_transactions',
            },
          ],
        },
        submitting_transactions: {
          invoke: {
            src: 'handleTransactions',
            onDone: {
              target: 'completed_transactions',
            },
            onError: {
              target: 'reviewing_transactions',
              actions: 'assignError',
            },
          },
          tags: 'loading',
        },
        completed_transactions: {
          type: 'final',
        },
        editing_transaction: {
          entry: modelAssign({
            transactionMode: 'edit',
          }),
          on: {
            REVIEW_TRANSACTIONS: {
              target: 'validating_transaction',
            },
          },
        },
        validating_transaction: {
          invoke: {
            src: 'validateTransaction',
            onDone: {
              actions: 'updateWithValidatedFustAmount',
              target: 'reviewing_transactions',
            },
            onError: {
              target: 'editing_transaction',
              actions: 'assignError',
            },
          },
          tags: 'loading',
        },
        dividing_transaction: {
          entry: modelAssign({
            transactionMode: 'split',
          }),
          on: {
            ASSIGN_UPDATED_FUST_AMOUNT: {
              actions: 'assignUpdatedFustAmount',
            },
            ASSIGN_SELECTED_TRANSACTION: {
              actions: 'assignSelectedTransaction',
            },
            VALIDATE_TRANSACTION: {
              target: 'validating_transaction',
            },
          },
        },
        submitting_divide_transactions: {
          invoke: {
            src: 'lateDivideTransaction',
            onDone: {
              target: 'completed_transactions',
            },
            onError: {
              target: 'reviewing_transactions',
              actions: 'assignError',
            },
          },
          tags: 'loading',
        },
      },
      on: {
        RESET_ERROR: {
          actions: 'resetError',
        },
      },
      exit: ['resetTransactionStatus'],
    },
    {
      guards: {
        isSplittedFustAmount: context => {
          return context.updatedFustAmount.length > 0
        },
        isNotSplittedFustAmount: context => {
          return context.updatedFustAmount.length === 0
        },
      },
      actions: {
        updateWithValidatedFustAmount: immerAssign((context, event) => {
          context.transactionsDataGridService.state.context.selectedTransactions = [
            {
              ...context.transactionsDataGridService.state.context
                .selectedTransactions[0],
              fustAantallen: event.data.data,
            },
          ]
        }),
        resetTransactionStatus: (context, event) => {
          if (event.type === 'xstate.stop') {
            context.transactionsDataGridService.state?.context.selectedTransactions.forEach(
              tx =>
                updateTransactionStatus(tx.id, {
                  NewStatus: TransactionStatus.Nieuw,
                })
            )
          }
        },
        assignError: assign((_context, event) => ({
          error: String((event.data as AxiosError).response?.data),
        })),
        resetError: assign(_ => ({ error: undefined })),
        assignSelectedTransaction: modelAssign((_context, event) => {
          return {
            selectedTransaction: event.transaction,
          }
        }),
        assignUpdatedFustAmount: modelAssign((_context, event) => {
          return {
            updatedFustAmount: event.updatedFustAmount,
          }
        }),
      },
      services: {
        lateDivideTransaction: async context => {
          const selectedTransaction =
            context.transactionsDataGridService.state.context.selectedTransactions[0]
          return lateDivideTransaction(
            selectedTransaction,
            String(context?.scannedFustPassContext?.scannedFustPass?.fustpasNummer),
            String(selectedTransaction.locatie)
          )
        },
        validateTransaction: async context => {
          return validateTransaction(
            context.selectedTransaction!.id,
            context.selectedTransaction!.klantnummer,
            {
              ...context.selectedTransaction,
              fustAantallen:
                context.transactionsDataGridService.state.context
                  .selectedTransactions[0].fustAantallen,
            } as FustTransaction
          )
        },
        handleTransactions: async context => {
          const {
            transactionsDataGridService: {
              state: {
                context: { selectedTransactions },
              },
            },
          } = context

          return batchHandleTransactions(
            selectedTransactions,
            String(context?.scannedFustPassContext?.scannedFustPass?.fustpasNummer),
            Number(context?.scannedFustPassContext?.scannedFustPass?.klant.nummer),
            TransactionType.UitgifteDock
          )
        },
      },
    }
  )

export const issuanceDockTransactionsService = interpret(
  issuanceDockTransactionsMachine
)
